// Tripwire only: agents/users can override by widening or clearing the scope.
//
// Binds a git checkout (main or linked worktree) to a SET of hubs and a SET of
// bases, so push/pull refuse to run against anything outside them. NOT a
// concurrency lock and NOT authorization — grants remain the only security
// boundary. It is a per-worktree routing guard against a prompt landing in the
// wrong checkout.
//
// **Guard only.** The scope never selects a target: it is consulted after a
// target has been resolved, and its only outcome is "carry on" or "refuse".
// §3.3's routing rules (`subtree-routing.ts`) do not read it.
//
// One file, two axes. It was two single-valued files — `wayai-binding` (hub) and
// `wayai-base-binding` (base) — over one parameterized module. Cardinality is
// what changed: post-consolidation the workspace is org-shaped (`hubs/`,
// `bases/`, `org/` side by side), so a task spanning two hubs, or a base serving
// several hubs, is ordinary work. A single-valued binding tripped on it and
// trained agents to clear the tripwire reflexively, which is worse than no
// tripwire at all (docs/reference/rekor-platform-contracts.md §3.3).
//
// The failure semantics are the ones the two-file version pinned, and they are
// load-bearing: a present-but-corrupt scope reads as EMPTY without falling back
// to the legacy files (a garbled file must not silently restore a stale
// binding), ENOENT reads as empty, and every other read error PROPAGATES so an
// unreadable scope can never masquerade as "no scope".

import { execFileSync } from 'node:child_process';
import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { PATH_SAFE_ID_RULE, isPathSafeId } from './base-id.js';
import { expected } from './expected.js';
import { UUID_RE } from './utils.js';

/** The two entity kinds a worktree can be scoped to — the workspace subtrees. */
export type ScopeAxis = 'hubs' | 'bases';

export interface WorktreeScope {
  hubs: string[];
  bases: string[];
}

/** The single scope file, under the per-worktree git dir. */
export const SCOPE_FILE = 'wayai-scope';

/** Pre-merge single-valued hub binding, read as a fallback and swept on write. */
export const LEGACY_HUB_SCOPE_FILE = 'wayai-binding';
/** The hub binding's own pre-rename name, from when this was called a "lock". */
export const LEGACY_HUB_SCOPE_FILE_PRE_RENAME = 'wayai-lock';
/** Pre-merge single-valued base binding. */
export const LEGACY_BASE_SCOPE_FILE = 'wayai-base-binding';

interface AxisDescriptor {
  /** Noun for user-facing messages, e.g. 'hub'. */
  noun: string;
  /** What an invalid id is called in the write-validation error. */
  idLabel: string;
  /**
   * Structural validity of an id on this axis. A hub id is a UUID; a base id is
   * a path-safe slug — strict enough that a garbled or multi-line legacy file
   * reads as unscoped, but deliberately NOT the API's mint rule, which would
   * refuse a base whose stored id predates it.
   */
  isValidId: (id: string) => boolean;
  /**
   * How this axis's id must be spelled as a `wayai use` selector. A bare UUID
   * routes to the hub subtree on its own; a base slug needs its subtree prefix,
   * or it would resolve to whichever subtree happens to hold a folder by that
   * name (`subtree-routing.ts`).
   */
  selectorFor: (id: string) => string;
  /** Single-valued files this axis migrates from, most-recent name first. */
  legacyFiles: string[];
}

const AXES: Record<ScopeAxis, AxisDescriptor> = {
  hubs: {
    noun: 'hub',
    idLabel: 'hub_id (must be a UUID)',
    isValidId: (id) => UUID_RE.test(id),
    selectorFor: (id) => id,
    legacyFiles: [LEGACY_HUB_SCOPE_FILE, LEGACY_HUB_SCOPE_FILE_PRE_RENAME],
  },
  bases: {
    noun: 'base',
    idLabel: `base id (${PATH_SAFE_ID_RULE})`,
    isValidId: isPathSafeId,
    selectorFor: (id) => `bases/${id}`,
    legacyFiles: [LEGACY_BASE_SCOPE_FILE],
  },
};

/** Both axes, in the order every message and every write-validation loop walks them. */
export const AXIS_ORDER: ScopeAxis[] = ['hubs', 'bases'];

/** The user-facing noun for an axis — 'hub' / 'base'. One mapping, not one per caller. */
export function axisNoun(axis: ScopeAxis): string {
  return AXES[axis].noun;
}

/**
 * The three ways out of a refusal, narrowest first.
 *
 * All three are offered deliberately. `--add` and a full reset are the two the
 * merge introduced, and both leave the tripwire WEAKER than the replace the
 * single-valued binding used to offer: `--add` keeps the wrong entity scoped,
 * and a bare `unbind` now clears the other axis too. A message whose entire job
 * is to stop reflexive tripwire-clearing must keep naming the narrow option.
 */
const ADD_COMMAND = 'wayai use --add';
const REPLACE_COMMAND = 'wayai use';
const CLEAR_COMMAND = 'wayai unbind';

// Cache keyed by cwd so tests that chdir between checkouts re-resolve.
let _gitDirCache: { cwd: string; gitDir: string | null } | undefined;

function resolveGitDir(): string | null {
  const cwd = process.cwd();
  if (_gitDirCache && _gitDirCache.cwd === cwd) return _gitDirCache.gitDir;

  let gitDir: string | null;
  try {
    // `--git-dir` resolves to the per-worktree git dir (`.git/worktrees/<name>`
    // for a linked worktree), which is what makes the scope worktree-local.
    const raw = execFileSync('git', ['rev-parse', '--git-dir'], {
      cwd,
      encoding: 'utf-8',
      stdio: ['pipe', 'pipe', 'pipe'],
    }).trim();
    gitDir = raw ? path.resolve(cwd, raw) : null;
  } catch {
    gitDir = null;
  }
  _gitDirCache = { cwd, gitDir };
  return gitDir;
}

/** Absolute path to this worktree's scope file, or null outside a git repo. */
export function getScopePath(): string | null {
  const gitDir = resolveGitDir();
  return gitDir ? path.join(gitDir, SCOPE_FILE) : null;
}

function legacyPaths(): string[] {
  const gitDir = resolveGitDir();
  if (!gitDir) return [];
  return AXIS_ORDER.flatMap((axis) => AXES[axis].legacyFiles).map((f) => path.join(gitDir, f));
}

/** A scope holding nothing — what an absent, corrupt, or repo-less read yields. */
export function emptyScope(): WorktreeScope {
  return { hubs: [], bases: [] };
}

/**
 * Parse one axis out of a loaded scope document.
 *
 * A missing or non-list value is an empty axis, and an entry failing the axis
 * grammar is dropped — the same "a garbled value is not an id" rule the
 * single-valued files applied, applied per entry. Duplicates collapse so the
 * set stays a set even if the file was hand-edited.
 */
function parseAxis(raw: unknown, axis: ScopeAxis): string[] {
  if (!Array.isArray(raw)) return [];
  const { isValidId } = AXES[axis];
  const out: string[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'string') continue;
    const id = entry.trim();
    if (!isValidId(id) || out.includes(id)) continue;
    out.push(id);
  }
  return out;
}

/**
 * The one legacy read: each pre-merge file seeds its own axis.
 *
 * Reached ONLY when the scope file is absent. A present-but-corrupt scope file
 * must not fall through to here — that would let a garbled write silently
 * restore a stale binding the user believes they replaced.
 */
function readLegacyAxis(axis: ScopeAxis): string[] {
  const gitDir = resolveGitDir();
  if (!gitDir) return [];
  const { isValidId, legacyFiles } = AXES[axis];
  for (const filename of legacyFiles) {
    let raw: string;
    try {
      raw = fs.readFileSync(path.join(gitDir, filename), 'utf-8');
    } catch (err) {
      if ((err as NodeJS.ErrnoException).code === 'ENOENT') continue;
      throw err;
    }
    // The FIRST file that exists decides, corrupt or not: falling through to an
    // older name on a garbled newer one is the same stale-restore hazard.
    const trimmed = raw.trim();
    return isValidId(trimmed) ? [trimmed] : [];
  }
  return [];
}

/**
 * This worktree's scope. Empty axes mean "unscoped on that axis" — which the
 * guard reads as "allow anything", never as "allow nothing".
 *
 * Surfaces non-ENOENT errors (EACCES, EISDIR) so an unreadable scope cannot
 * masquerade as unscoped. `wayai status` is the one caller that catches them:
 * a diagnostic command must not crash on the state it is diagnosing.
 */
export function readScope(): WorktreeScope {
  const scopePath = getScopePath();
  if (!scopePath) return emptyScope();

  let raw: string;
  try {
    raw = fs.readFileSync(scopePath, 'utf-8');
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code === 'ENOENT') {
      return { hubs: readLegacyAxis('hubs'), bases: readLegacyAxis('bases') };
    }
    throw err;
  }

  let doc: unknown;
  try {
    doc = yaml.load(raw);
  } catch {
    return emptyScope();
  }
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return emptyScope();
  const map = doc as Record<string, unknown>;
  return { hubs: parseAxis(map.hubs, 'hubs'), bases: parseAxis(map.bases, 'bases') };
}

/**
 * Truly best-effort: a leftover legacy file is harmless once the scope file
 * exists (`readScope` only consults them when it is absent), so failing to
 * remove one must NOT fail a write that already persisted. Swallows every error.
 */
function sweepLegacyFiles(): void {
  for (const legacyPath of legacyPaths()) {
    try {
      fs.unlinkSync(legacyPath);
    } catch {
      // ignore — migration cleanup is optional
    }
  }
}

/**
 * Persist the whole scope, validating every id, then sweep the legacy files.
 *
 * Every failure it raises is `expected`-tagged, because every one of them is a
 * user or environment condition rather than a WayAI bug: an id the grammar
 * refuses, a cwd outside any git repository, an unwritable git dir. The commands
 * that call this deliberately do NOT wrap store errors — a catch-all there would
 * swallow a genuine crash too — so the tag has to be applied at the throw site
 * or `shouldReportToSentry` files an autofix ticket for `cd ~ && wayai use <id>`.
 * The deprecated `wayai bases use` alias has always done this by wrapping; doing
 * it here means the front-door commands are not weaker than the alias they
 * supersede.
 */
export function writeScope(scope: WorktreeScope): void {
  for (const axis of AXIS_ORDER) {
    const { isValidId, idLabel } = AXES[axis];
    for (const id of scope[axis]) {
      if (!isValidId(id)) throw expected(`Invalid ${idLabel}: ${id}`);
    }
  }
  const scopePath = getScopePath();
  if (!scopePath) {
    throw expected('Not inside a git repository — cannot write the worktree scope.');
  }
  // `flowLevel: 1` keeps each axis on one line (`hubs: [<id>, <id>]`), which is
  // what a human skims and what the docs show.
  const body = yaml.dump({ hubs: scope.hubs, bases: scope.bases }, { flowLevel: 1 });

  // Written tmp-then-rename, which a single-valued binding never needed: one
  // document now carries BOTH axes, so a write torn by a killed process would
  // leave a file that fails to parse — and a scope that fails to parse reads as
  // empty, disarming the tripwire on both axes at once and letting the next
  // write persist that emptiness. `rename` within the same directory is atomic,
  // so a reader sees either the old document or the new one, never a partial.
  const tmpPath = `${scopePath}.tmp-${process.pid}`;
  try {
    fs.writeFileSync(tmpPath, body, 'utf-8');
    fs.renameSync(tmpPath, scopePath);
  } catch (err) {
    try {
      fs.unlinkSync(tmpPath);
    } catch {
      // ignore — the temp file is already gone, or was never created
    }
    // An errno-bearing failure is the environment answering — a read-only git
    // dir, a full disk — never a defect in this module, so it prints and exits
    // instead of filing a Sentry event. Anything WITHOUT a `code` (a TypeError
    // from this function, say) is a real bug and is rethrown untouched, which is
    // why this discriminates rather than tagging the whole catch.
    throw asExpectedEnvironmentError(err);
  }
  sweepLegacyFiles();
}

/** Tag an errno-bearing fs failure as expected; pass a programming error through. */
function asExpectedEnvironmentError(err: unknown): unknown {
  if (err instanceof Error && typeof (err as NodeJS.ErrnoException).code === 'string') {
    return expected(err.message);
  }
  return err;
}

/**
 * Replace one axis outright, leaving the other untouched. Returns the axis as
 * persisted, so a caller reporting the new state never re-reads the file it just
 * wrote — a second read is one more chance to print something else's answer.
 */
export function setScopeAxis(axis: ScopeAxis, ids: string[]): string[] {
  writeScope({ ...readScope(), [axis]: ids });
  return ids;
}

/**
 * Append to one axis. Returns the axis as persisted, or null when the id was
 * already in the set (nothing written).
 */
export function addToScope(axis: ScopeAxis, id: string): string[] | null {
  const scope = readScope();
  if (scope[axis].includes(id)) return null;
  const next = [...scope[axis], id];
  writeScope({ ...scope, [axis]: next });
  return next;
}

/**
 * Drop one entry from one axis. Returns the axis as persisted, or null when the
 * id was not in the set (nothing written).
 */
export function removeFromScope(axis: ScopeAxis, id: string): string[] | null {
  const scope = readScope();
  if (!scope[axis].includes(id)) return null;
  const next = scope[axis].filter((entry) => entry !== id);
  writeScope({ ...scope, [axis]: next });
  return next;
}

/**
 * Clear the whole scope — both axes. Returns true when something was removed.
 * Removes the legacy files too, so a clear cannot be undone by a stale one.
 * Surfaces non-ENOENT errors.
 */
export function clearScope(): boolean {
  const scopePath = getScopePath();
  let removed = false;
  for (const target of [...(scopePath ? [scopePath] : []), ...legacyPaths()]) {
    try {
      fs.unlinkSync(target);
      removed = true;
    } catch (err) {
      if ((err as NodeJS.ErrnoException).code !== 'ENOENT') throw err;
    }
  }
  return removed;
}

/**
 * "hub X" / "hubs X, Y" — one axis as every message names it. Exported so the
 * refusal and the unbind confirmation spell the same state the same way; two
 * renderers would show a user two spellings of the scope they just changed.
 */
export function describeAxis(axis: ScopeAxis, ids: string[]): string {
  const { noun } = AXES[axis];
  return ids.length === 1 ? `${noun} ${ids[0]}` : `${noun}s ${ids.join(', ')}`;
}

/** Both axes, non-empty ones only — "hub X; bases crm, billing". Empty when unscoped. */
export function describeScope(scope: WorktreeScope): string {
  return AXIS_ORDER
    .filter((axis) => scope[axis].length > 0)
    .map((axis) => describeAxis(axis, scope[axis]))
    .join('; ');
}

/**
 * The guard. A resolved target outside a non-empty axis is refused — non-zero
 * exit, message on stderr.
 *
 * **A refusal is NOT side-effect-free, and callers must not assume it is.** This
 * guard needs a RESOLVED id, so by the time it runs the command has already done
 * whatever produced that id. On the paths that call it, that includes reading the
 * out-of-set entity's own config — over the network (`pull.ts` has the whole
 * `HubAsCodePayload` in hand) and from disk (`publish.ts` parses the hub folder,
 * `data/config-as-code/sync.ts` the base folder) — plus repo-level scaffolding
 * unrelated to the target (`push.ts` seeds `AGENTS.md`/`CLAUDE.md`). What it does
 * guarantee: no MUTATING request reaches the out-of-set entity, and its workspace
 * folder is not written. The stronger "before any network call and before any
 * file write" placement belongs to `requireSubtree` in `subtree-routing.ts`,
 * which decides from arguments and disk alone
 * (docs/reference/rekor-platform-contracts.md §3.3).
 *
 * Deliberately prints and exits rather than throwing: this is an expected
 * routing condition, not a bug, so it must stay out of the Sentry pipeline.
 */
export function assertInScope(axis: ScopeAxis, resolvedId: string, label?: string): void {
  const scoped = readScope()[axis];
  if (scoped.length === 0) return;
  if (scoped.includes(resolvedId)) return;

  const { noun, selectorFor } = AXES[axis];
  const selector = selectorFor(resolvedId);
  const target = label ? `${label} (${resolvedId})` : resolvedId;
  console.error(
    [
      `This worktree is scoped to ${describeAxis(axis, scoped)}, but the command resolved to ${target}.`,
      '',
      'This usually means a prompt was routed to the wrong worktree.',
      'Confirm with the user before changing this scope. To override:',
      `  ${REPLACE_COMMAND} ${selector}   # scope this worktree to that ${noun} INSTEAD`,
      `  ${ADD_COMMAND} ${selector}   # scope it to that ${noun} AS WELL`,
      `  ${CLEAR_COMMAND}   # clear the whole worktree scope (both hubs and bases)`,
    ].join('\n'),
  );
  process.exit(1);
}

/**
 * The creation guard: a new hub/base in a worktree already scoped on that axis
 * is the same wrong-worktree signal.
 *
 * **Neither `wayai use` escape is offered here — do not add one.** The reason is
 * NOT that they fail on an idless folder. That is true on the hub side only
 * (`resolveHubSelectorToId` redirects to `wayai create`); the base side is
 * deliberately total, so `wayai use --add bases/<name>` on a folder with no
 * `base_id` SUCCEEDS and writes the folder name into the scope. The reason is
 * that this guard refuses on a non-empty axis whatever ids that axis holds, so
 * widening or replacing cannot unblock a creation either way — offering one
 * would print a recovery command that leaves the create refused exactly as
 * before, and on the base side also leaves a scope entry naming a base that does
 * not exist (docs/reference/rekor-platform-contracts.md §3.3).
 *
 * Fail-secure: `readScope` errors propagate (matches `assertInScope`).
 */
export function assertScopeAllowsCreation(axis: ScopeAxis, newName: string): void {
  const scoped = readScope()[axis];
  if (scoped.length === 0) return;

  const { noun } = AXES[axis];
  console.error(
    [
      `This worktree is scoped to ${describeAxis(axis, scoped)}, but you're about to create a new ${noun} "${newName}" here.`,
      '',
      'This usually means a prompt was routed to the wrong worktree.',
      'Confirm with the user before clearing this scope. To override:',
      `  ${CLEAR_COMMAND}   # clear the whole worktree scope (both hubs and bases)`,
    ].join('\n'),
  );
  process.exit(1);
}

/**
 * Seed an EMPTY axis from the first successful push/pull/create in a worktree.
 *
 * Never appends to a non-empty axis: widening is a routing decision and only
 * `wayai use --add` makes it. Best-effort — errors don't fail the parent
 * operation, which already succeeded.
 */
export function seedScopeIfEmpty(axis: ScopeAxis, id: string): boolean {
  try {
    if (readScope()[axis].length > 0) return false;
    setScopeAxis(axis, [id]);
    console.log(
      `Worktree bound to ${AXES[axis].noun} ${id} (run \`${CLEAR_COMMAND}\` to clear).`,
    );
    return true;
  } catch (err) {
    console.warn(
      `  Warning: could not bind worktree (${err instanceof Error ? err.message : String(err)})`,
    );
    return false;
  }
}
